import { useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Star, ArrowUp, ArrowDown, Trash2, Image as ImageIcon, Film, UploadCloud, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { MEDIA_CATEGORIES } from '@pms/shared';
import type { MediaCategory, MediaCategoryGroup } from '@pms/shared';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { usePropertyEditorApi } from './api-context';
import { extractErrorMessage } from '@/lib/error';
import { showToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import type { PcMedia } from '@/services/api/property-content.api';
import { fieldInputClass } from './fields';

/** Categories grouped by their `group` for <optgroup> rendering. */
const CATEGORY_GROUPS: { group: MediaCategoryGroup; items: MediaCategory[] }[] = (() => {
  const order: MediaCategoryGroup[] = [];
  const byGroup = new Map<MediaCategoryGroup, MediaCategory[]>();
  for (const c of MEDIA_CATEGORIES) {
    if (!byGroup.has(c.group)) {
      byGroup.set(c.group, []);
      order.push(c.group);
    }
    byGroup.get(c.group)!.push(c);
  }
  return order.map((group) => ({ group, items: byGroup.get(group)! }));
})();

const CATEGORY_LABELS = new Map(MEDIA_CATEGORIES.map((c) => [c.key, c.label]));

/**
 * Renders a media image whose `src` is EITHER an inline `data:`/`http(s):` URL
 * (admin console — bytes are already in the aggregate) OR a relative byte-endpoint
 * ref like `/my-property/:id/media/:mid/raw` (hotel listing editor — the aggregate
 * ships metadata only, so images load lazily). For a ref we fetch through the
 * authed `api` client (a bare <img src> can't send the Bearer token) and hand the
 * blob to an object URL, revoking it on unmount / src change to avoid leaks.
 */
function AuthImage({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const inline = /^(data:|https?:|blob:)/.test(src);
  const [objUrl, setObjUrl] = useState<string | null>(inline ? src : null);

  useEffect(() => {
    if (/^(data:|https?:|blob:)/.test(src)) {
      setObjUrl(src);
      return;
    }
    let revoked = false;
    let created: string | null = null;
    setObjUrl(null);
    api
      .get<Blob>(src, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        created = URL.createObjectURL(res.data);
        setObjUrl(created);
      })
      .catch(() => {
        /* leave placeholder on failure */
      });
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [src]);

  if (!objUrl) {
    return (
      <div className={cn('flex items-center justify-center bg-gray-100 text-gray-300', className)}>
        <Loader2 size={20} className="animate-spin" aria-hidden="true" />
      </div>
    );
  }
  return <img src={objUrl} alt={alt} className={className} />;
}

/** category key → grouping label, for organising the gallery into sections. */
const CATEGORY_TO_GROUP = new Map(MEDIA_CATEGORIES.map((c) => [c.key, c.group]));
/** Group render order (matches the enum order in MEDIA_CATEGORIES). */
const GROUP_ORDER: MediaCategoryGroup[] = CATEGORY_GROUPS.map((g) => g.group);
const UNCATEGORISED = 'Uncategorised';

/** Bucket the (already globally-sorted) media into category-group sections. */
function groupByCategory(
  items: PcMedia[],
): { group: string; items: PcMedia[] }[] {
  const buckets = new Map<string, PcMedia[]>();
  for (const m of items) {
    const group = (m.category && CATEGORY_TO_GROUP.get(m.category)) || UNCATEGORISED;
    if (!buckets.has(group)) buckets.set(group, []);
    buckets.get(group)!.push(m);
  }
  const ordered: { group: string; items: PcMedia[] }[] = [];
  for (const g of GROUP_ORDER) {
    if (buckets.has(g)) ordered.push({ group: g, items: buckets.get(g)! });
  }
  if (buckets.has(UNCATEGORISED)) {
    ordered.push({ group: UNCATEGORISED, items: buckets.get(UNCATEGORISED)! });
  }
  return ordered;
}

/**
 * Read an image File and return a downscaled JPEG data: URL. Keeps the longest
 * edge ≤ maxDim and re-encodes at the given quality so a 4 MB phone photo lands
 * at ~150–400 KB — small enough to store inline and serve to the storefront.
 * Falls back to the original data URL if canvas isn't available.
 */
/** Read any file to a data URL as-is (used for video; images are downscaled). */
async function fileToDataUrl(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

async function fileToDownscaledDataUrl(
  file: File,
  maxDim = 1600,
  quality = 0.82,
): Promise<string> {
  const original = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Could not decode image'));
    el.src = original;
  });
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const scale = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return original;
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
}

function CategoryOptions() {
  return (
    <>
      <option value="">Uncategorised</option>
      {CATEGORY_GROUPS.map(({ group, items }) => (
        <optgroup key={group} label={group}>
          {items.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
}

export function PhotosTab({ propertyId, media }: { propertyId: string; media: PcMedia[] }) {
  const [category, setCategory] = useState('');
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<PcMedia | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);

  const {
    useCreateMedia,
    useSetMediaCategory,
    useReorderMedia,
    useSetMediaCover,
    useDeleteMedia,
  } = usePropertyEditorApi();
  const createMutation = useCreateMedia(propertyId);
  const categoryMutation = useSetMediaCategory(propertyId);
  const reorderMutation = useReorderMedia(propertyId);
  const coverMutation = useSetMediaCover(propertyId);
  const deleteMutation = useDeleteMedia(propertyId);

  // The editor manages PROPERTY-scoped media (cover/reorder applies per scope).
  const propertyMedia = useMemo(
    () =>
      media
        .filter((m) => m.scope === 'property')
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [media],
  );

  // Gallery organised into labelled category sections (Room, Property, …).
  const groupedMedia = useMemo(() => groupByCategory(propertyMedia), [propertyMedia]);

  // Largest inline video we accept until object-storage upload is wired: a
  // base64 data URL inflates ~33%, and the backend caps a media url at ~8 MB.
  const MAX_VIDEO_BYTES = 5_000_000;

  async function handleFiles(files: FileList | File[]) {
    const list = Array.from(files).filter(
      (f) => f.type.startsWith('image/') || f.type.startsWith('video/'),
    );
    if (list.length === 0) {
      setError('Please choose image or video files.');
      return;
    }
    setError('');
    setUploading(true);
    let ok = 0;
    let failed = 0;
    let tooBig = 0;
    for (let i = 0; i < list.length; i++) {
      setProgress({ current: i + 1, total: list.length });
      const file = list[i]!;
      const isVideo = file.type.startsWith('video/');
      try {
        if (isVideo && file.size > MAX_VIDEO_BYTES) {
          tooBig++;
          continue;
        }
        const dataUrl = isVideo
          ? await fileToDataUrl(file)
          : await fileToDownscaledDataUrl(file);
        await createMutation.mutateAsync({
          type: isVideo ? 'video' : 'image',
          scope: 'property',
          url: dataUrl,
          category: category || undefined,
        });
        ok++;
      } catch {
        failed++;
      }
    }
    setUploading(false);
    setProgress(null);
    if (ok > 0) showToast(`${ok} file${ok > 1 ? 's' : ''} uploaded · live on storefront`);
    if (tooBig > 0)
      showToast(
        `${tooBig} video${tooBig > 1 ? 's' : ''} skipped — keep videos under 5 MB for now`,
        'error',
      );
    if (failed > 0) showToast(`${failed} file${failed > 1 ? 's' : ''} couldn't be uploaded`, 'error');
  }

  async function changeCategory(m: PcMedia, value: string) {
    try {
      await categoryMutation.mutateAsync({ mediaId: m.id, category: value || null });
      showToast('Category updated');
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
    }
  }

  // Reorder within a category section: swap the two group-adjacent items in the
  // global order and persist the full ordering (backend stores a flat sortOrder).
  async function moveInGroup(groupItems: PcMedia[], index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= groupItems.length) return;
    const a = groupItems[index]!;
    const b = groupItems[target]!;
    const order = propertyMedia.map((m) => m.id);
    const ia = order.indexOf(a.id);
    const ib = order.indexOf(b.id);
    if (ia === -1 || ib === -1) return;
    [order[ia], order[ib]] = [order[ib]!, order[ia]!];
    try {
      await reorderMutation.mutateAsync(order);
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
    }
  }

  async function setCover(m: PcMedia) {
    try {
      await coverMutation.mutateAsync(m.id);
      showToast('Cover updated');
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      showToast('Media removed');
      setDeleteTarget(null);
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
      setDeleteTarget(null);
    }
  }

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-1 text-base font-semibold text-gray-900">Upload photos &amp; video</h2>
        <p className="mb-4 text-sm text-gray-500">
          Choose photos or videos from your device (select as many as you like) or drag &amp; drop
          them below. Photos are optimised in your browser and published to the storefront
          automatically.
        </p>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
            {error}
          </div>
        )}

        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploading) setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            if (!uploading) void handleFiles(e.dataTransfer.files);
          }}
          className={cn(
            'flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors',
            dragActive ? 'border-brand-400 bg-brand-50' : 'border-gray-300 bg-gray-50',
          )}
        >
          <UploadCloud size={32} className="mb-3 text-gray-400" aria-hidden="true" />
          <p className="text-sm text-gray-600">Drag &amp; drop photos or videos here</p>
          <p className="mb-4 text-xs text-gray-400">JPG, PNG, WebP or MP4 — multiple allowed</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <ImagePlus size={16} aria-hidden="true" />
            Choose photos
          </Button>
          {uploading && progress && (
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-gray-600" role="status" aria-live="polite">
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              Uploading {progress.current} of {progress.total}…
            </p>
          )}
        </div>

        <div className="mt-4 max-w-xs">
          <label htmlFor="batch-category" className="block text-sm font-medium text-gray-700 mb-1">
            Apply category to uploads <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <select id="batch-category" value={category} onChange={(e) => setCategory(e.target.value)} className={fieldInputClass}>
            <CategoryOptions />
          </select>
        </div>

      </section>

      {propertyMedia.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white">
          <EmptyState icon={ImageIcon} title="No photos yet" description="Upload photos from your computer above to build the gallery." />
        </div>
      ) : (
        <div className="space-y-8">
          {groupedMedia.map(({ group, items }) => (
            <section key={group} aria-labelledby={`photo-group-${group}`}>
              <div className="mb-3 flex items-center gap-2 border-b border-gray-100 pb-2">
                <h3 id={`photo-group-${group}`} className="text-sm font-semibold text-gray-900">
                  {group}
                </h3>
                <span className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                  {items.length}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((m, index) => (
                  <div key={m.id} className={cn('overflow-hidden rounded-xl border bg-white shadow-sm', m.isCover ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-200')}>
                    <div className="relative aspect-video bg-gray-100">
                      {m.type === 'image' ? (
                        <AuthImage src={m.url} alt={m.caption ?? 'Property media'} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-400">
                          <Film size={32} aria-hidden="true" />
                        </div>
                      )}
                      {m.isCover && (
                        <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white">
                          <Star size={12} aria-hidden="true" /> Cover
                        </span>
                      )}
                      {m.category && (
                        <span className="absolute right-2 top-2 inline-flex items-center rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white">
                          {CATEGORY_LABELS.get(m.category) ?? m.category}
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="truncate text-sm text-gray-700" title={m.caption ?? 'Photo'}>
                        {m.caption || 'Photo'}
                      </p>
                      <div className="mt-2">
                        <label htmlFor={`media-cat-${m.id}`} className="sr-only">Category</label>
                        <select
                          id={`media-cat-${m.id}`}
                          value={m.category ?? ''}
                          onChange={(e) => void changeCategory(m, e.target.value)}
                          disabled={categoryMutation.isPending}
                          className={cn(fieldInputClass, 'text-sm')}
                        >
                          <CategoryOptions />
                        </select>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => moveInGroup(items, index, -1)} disabled={index === 0 || reorderMutation.isPending} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30" aria-label="Move up">
                            <ArrowUp size={15} aria-hidden="true" />
                          </button>
                          <button type="button" onClick={() => moveInGroup(items, index, 1)} disabled={index === items.length - 1 || reorderMutation.isPending} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30" aria-label="Move down">
                            <ArrowDown size={15} aria-hidden="true" />
                          </button>
                        </div>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => setCover(m)} disabled={m.isCover || coverMutation.isPending} className="p-2 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 disabled:opacity-30" aria-label="Set as cover">
                            <Star size={15} aria-hidden="true" />
                          </button>
                          <button type="button" onClick={() => setDeleteTarget(m)} className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete media">
                            <Trash2 size={15} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete this media?"
        message="It will be removed from the storefront gallery. This can't be undone."
        confirmLabel="Delete"
        isLoading={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
